import { Node } from '@tiptap/core';

export const VariableSelect = Node.create({
  name: 'variableSelect',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      variable: { default: null },
      value: { default: '' },
      placeholder: { default: '' },
      required: { default: false },
      options: { default: [] },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span.variable-select',
        getAttrs: (dom) => ({
          variable: dom.dataset.variable || null,
          value: dom.dataset.value || '',
          placeholder: dom.dataset.placeholder || '',
          required: dom.dataset.required || false,
          options: dom.dataset.options ? dom.dataset.options.split(',') : [],
        }),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      {
        class: 'variable-select',
        'data-variable': HTMLAttributes.variable,
        'data-value': HTMLAttributes.value,
      },
      HTMLAttributes.value || '',
    ];
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      const wrapper = document.createElement('span');
      wrapper.classList.add('variable-select');
      wrapper.dataset.variable = node.attrs.variable;
      wrapper.style.position = 'relative';

      const display = document.createElement('div');
      display.classList.add('variable-select-control');
      display.textContent = node.attrs.value || node.attrs.placeholder || 'Select an option';
      wrapper.appendChild(display);

      const chevron = document.createElement('span');
      chevron.classList.add('chevron');
      chevron.textContent = '▾';
      chevron.style.marginLeft = '12px';
      display.appendChild(chevron);

      const dropdown = document.createElement('div');
      dropdown.classList.add('variable-select-dropdown');
      wrapper.appendChild(dropdown);

      const hasValue = !!node.attrs.value;

      // Populate options
      (node.attrs.options || []).forEach((opt) => {
        const value = typeof opt === 'object' ? opt.value || opt.label : opt;
        const label = typeof opt === 'object' ? opt.label || opt.value : opt;

        const item = document.createElement('div');
        item.classList.add('variable-select-dropdown-option');
        item.textContent = label;
        item.dataset.value = value;

        if (hasValue && value === node.attrs.value) {
          item.style.color = '#6222e3';
        }

        display.style.color = hasValue ? '#000' : '#6a6765';

        item.addEventListener('mouseenter', () => (item.style.background = '#f0f0f0'));
        item.addEventListener('mouseleave', () => (item.style.background = '#fff'));

        item.addEventListener('click', (e) => {
          e.stopPropagation();
          const { tr } = editor.state;
          tr.setNodeMarkup(getPos(), undefined, { ...node.attrs, value });
          editor.view.dispatch(tr);
          display.textContent = label;
          display.appendChild(chevron);
          dropdown.style.display = 'none';
          dropdown.classList.remove('open');
          wrapper.classList.remove('selected');
        });

        dropdown.appendChild(item);
      });

      // Toggle dropdown
      display.addEventListener('click', (e) => {
        e.stopPropagation();

        // Close other dropdowns
        document.querySelectorAll('.variable-select-dropdown.open').forEach((dd) => {
          if (dd !== dropdown) {
            dd.style.display = 'none';
            dd.classList.remove('open');
            dd.parentElement?.classList.remove('selected');
          }
        });

        const open = dropdown.classList.toggle('open');

        if (open) {
          requestAnimationFrame(() => {
            updateArrows();
            dropdown.addEventListener('scroll', updateArrows);
          });

          // --- Container boundaries ---
          const container = wrapper.closest('.assist-input');
          if (!container) return;

          const displayRect = display.getBoundingClientRect();

          const spaceAbove = displayRect.top - 42;

          // Apply max-height using calc for safety
          dropdown.style.maxHeight = `calc(${Math.max(120, spaceAbove)}px)`; // // `${Math.max(0, maxHeight - 4)}px`; // leave small padding

          dropdown.style.top = 'auto';
          dropdown.style.bottom = `${display.offsetHeight + 2}px`;

          dropdown.style.display = 'block';
          dropdown.scrollTop = 0;
          wrapper.classList.add('selected');
        } else {
          dropdown.style.display = 'none';
          wrapper.classList.remove('selected');
        }
      });

      let doc = null;
      try {
        // Only access if editor.view exists
        doc = editor?.view?.dom?.ownerDocument || window.document;
      } catch {
        doc = window.document;
      }

      const handleOutsideClick = (e) => {
        if (!wrapper.contains(e.target)) {
          dropdown.style.display = 'none';
          dropdown.classList.remove('open');
          wrapper.classList.remove('selected');
        }
      };

      doc.addEventListener('click', handleOutsideClick, true);

      const updateArrows = () => {
        const scrollTop = dropdown.scrollTop;
        const scrollHeight = dropdown.scrollHeight;
        const clientHeight = dropdown.clientHeight;

        if (scrollTop === 0) {
          dropdown.classList.toggle('scroll-bottom', scrollTop + clientHeight < scrollHeight);
        } else {
          dropdown.classList.remove('scroll-bottom');
        }
      };

      return {
        dom: wrapper,
        contentDOM: null,
        stopEvent: (event) => wrapper.contains(event.target),
        destroy: () => {
          doc.removeEventListener('click', handleOutsideClick, true);
        },
      };
    };
  },
});
